import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tooltip,
  CircularProgress,
  Chip,
  Badge,
  Collapse,
  Divider,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
  Tune as TuneIcon,
  CallReceived as IncomingIcon,
  CallMade as OutgoingIcon
} from '@mui/icons-material';
import { UserMarkingCodeFilters, UserFilterStatusOption, UserFilterTypeOption, DirectionType } from '../types';

interface UserMarkingCodesToolbarProps {
  filters: UserMarkingCodeFilters;
  isLoading: boolean;
  userRole?: string;
  storages: string[];
  statuses: UserFilterStatusOption[];
  types: UserFilterTypeOption[];
  onFilterChange: (newFilters: Partial<UserMarkingCodeFilters>) => void;
  onRefresh: () => void;
  onExport: () => void;
  onResetFilters: () => void;
}

export const UserMarkingCodesToolbar: React.FC<UserMarkingCodesToolbarProps> = ({
  filters,
  isLoading,
  userRole = 'user',
  storages,
  statuses,
  types,
  onFilterChange,
  onRefresh,
  onExport,
  onResetFilters
}) => {
  const theme = useTheme();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const isStorage = userRole === 'storage';

  // Количество активных фильтров в раскрывающейся панели
  const advancedActiveCount = useMemo(() => {
    let n = 0;
    if (filters.type !== 'all') n += 1;
    if (filters.status !== 'all') n += 1;
    if (filters.selectedStorage) n += 1;
    if (filters.dateFrom || filters.dateTo) n += 1;
    return n;
  }, [filters.type, filters.status, filters.selectedStorage, filters.dateFrom, filters.dateTo]);

  const hasAnyChip = Boolean(
    (!isStorage && filters.direction !== 'all') ||
    filters.selectedStorage ||
    filters.type !== 'all' ||
    filters.status !== 'all' ||
    filters.dateFrom ||
    filters.dateTo
  );

  const activeTypeName = useMemo(() => {
    if (filters.type === 'all') return null;
    const found = types.find((t) => String(t.zid) === String(filters.type));
    return found ? found.nameRu : String(filters.type);
  }, [filters.type, types]);

  const activeStatusName = useMemo(() => {
    if (filters.status === 'all') return null;
    const found = statuses.find((s) => String(s.zid) === String(filters.status));
    return found ? found.nameRu : String(filters.status);
  }, [filters.status, statuses]);

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: 'background.paper'
        }}
      >
        {/* ── Основная строка: поиск + направление + кнопка «Ещё фильтры» + действия ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', flexShrink: 0 }}>
            <FilterListIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Фильтр:
            </Typography>
          </Box>

          {/* Быстрый поиск */}
          <TextField
            size="small"
            placeholder="Поиск по коду маркировки, № документа, товару..."
            value={filters.search}
            onChange={(e) => onFilterChange({ search: e.target.value })}
            sx={{ minWidth: 280, flexGrow: { xs: 1, sm: 0 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
          />

          {/* Переключатель Входящие / Исходящие для обычной аптеки */}
          {!isStorage && (
            <ToggleButtonGroup
              size="small"
              value={filters.direction}
              exclusive
              onChange={(_, val) => {
                if (val) onFilterChange({ direction: val as DirectionType });
              }}
              sx={{
                height: 40,
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  gap: 0.5
                }
              }}
            >
              <ToggleButton value="all">
                Все
              </ToggleButton>
              <ToggleButton value="incoming" sx={{ '&.Mui-selected': { color: 'success.dark', bgcolor: alpha(theme.palette.success.main, 0.12) } }}>
                <IncomingIcon sx={{ fontSize: 16, color: 'success.main' }} />
                Входящие
              </ToggleButton>
              <ToggleButton value="outgoing" sx={{ '&.Mui-selected': { color: 'info.dark', bgcolor: alpha(theme.palette.info.main, 0.12) } }}>
                <OutgoingIcon sx={{ fontSize: 16, color: 'info.main' }} />
                Исходящие
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Переключатель дополнительных фильтров с бейджем */}
          <Badge badgeContent={advancedActiveCount} color="primary">
            <Tooltip title={advancedOpen ? 'Скрыть фильтры' : 'Показать дополнительные фильтры'}>
              <Button
                variant={advancedOpen ? 'contained' : 'outlined'}
                color={advancedOpen ? 'primary' : 'inherit'}
                size="small"
                onClick={() => setAdvancedOpen((o) => !o)}
                sx={{
                  height: 40,
                  minWidth: 40,
                  width: 40,
                  p: 0,
                  borderRadius: 1.5,
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }}
              >
                <TuneIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Badge>

          <Box sx={{ flexGrow: 1 }} />

          {/* ── Компактная группа действий ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Tooltip title="Сбросить все фильтры">
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={onResetFilters}
                sx={{
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  borderColor: '#c4c4c4',
                  '&:hover': { borderColor: '#b0b0b0', bgcolor: 'action.hover' }
                }}
              >
                Сброс
              </Button>
            </Tooltip>

            <Tooltip title="Обновить данные">
              <Button
                variant="contained"
                size="small"
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={onRefresh}
                disabled={isLoading}
                sx={{
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 2
                }}
              >
                Обновить
              </Button>
            </Tooltip>

            <Tooltip title="Выгрузка данных в Excel">
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={onExport}
                sx={{
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  color: 'success.main',
                  borderColor: 'success.main',
                  '&:hover': {
                    borderColor: 'success.dark',
                    bgcolor: alpha(theme.palette.success.main, 0.04)
                  }
                }}
              >
                Выгрузка
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Чипы активных фильтров ── */}
        {hasAnyChip && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            {!isStorage && filters.direction !== 'all' && (
              <Chip
                size="small"
                icon={filters.direction === 'incoming' ? <IncomingIcon sx={{ fontSize: '14px !important' }} /> : <OutgoingIcon sx={{ fontSize: '14px !important' }} />}
                label={`Направление: ${filters.direction === 'incoming' ? 'Входящие' : 'Исходящие'}`}
                onDelete={() => onFilterChange({ direction: 'all' })}
                sx={{
                  bgcolor: filters.direction === 'incoming' ? 'rgba(16,185,129,0.08)' : 'rgba(59,130,246,0.08)',
                  color: filters.direction === 'incoming' ? '#059669' : '#2563eb',
                  fontWeight: 600
                }}
              />
            )}

            {filters.selectedStorage && (
              <Chip
                size="small"
                label={`Склад: ${filters.selectedStorage}`}
                onDelete={() => onFilterChange({ selectedStorage: null })}
                sx={{ bgcolor: 'rgba(14,165,233,0.08)', color: '#0284c7', fontWeight: 600 }}
              />
            )}

            {activeTypeName && (
              <Chip
                size="small"
                label={`Тип: ${activeTypeName}`}
                onDelete={() => onFilterChange({ type: 'all' })}
                sx={{ bgcolor: 'rgba(16,185,129,0.08)', color: '#10b981', fontWeight: 600 }}
              />
            )}

            {activeStatusName && (
              <Chip
                size="small"
                label={`Статус: ${activeStatusName}`}
                onDelete={() => onFilterChange({ status: 'all' })}
                sx={{ bgcolor: 'rgba(249,115,22,0.08)', color: '#ea580c', fontWeight: 600 }}
              />
            )}

            {(filters.dateFrom || filters.dateTo) && (
              <Chip
                size="small"
                label={`Период: ${filters.dateFrom || '…'} - ${filters.dateTo || '…'}`}
                onDelete={() => onFilterChange({ dateFrom: '', dateTo: '' })}
                sx={{ bgcolor: 'rgba(245,158,11,0.08)', color: '#d97706', fontWeight: 600 }}
              />
            )}
          </Box>
        )}

        {/* ── Раскрывающаяся панель доп. фильтров ── */}
        <Collapse in={advancedOpen} timeout={200} unmountOnExit>
          <Divider sx={{ my: 1.25 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            {/* Тип */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Тип</InputLabel>
              <Select
                value={filters.type}
                label="Тип"
                onChange={(e) => onFilterChange({ type: e.target.value })}
              >
                {types.map((t) => (
                  <MenuItem key={String(t.zid)} value={t.zid}>
                    {t.nameRu}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Статус */}
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Статус</InputLabel>
              <Select
                value={filters.status}
                label="Статус"
                onChange={(e) => onFilterChange({ status: e.target.value })}
              >
                {statuses.map((s) => (
                  <MenuItem key={String(s.zid)} value={s.zid}>
                    {s.nameRu}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Склад */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Склад</InputLabel>
              <Select
                value={filters.selectedStorage || 'all'}
                label="Склад"
                onChange={(e) => onFilterChange({ selectedStorage: e.target.value === 'all' ? null : e.target.value })}
              >
                <MenuItem value="all">Все склады</MenuItem>
                {storages.map((st) => (
                  <MenuItem key={st} value={st}>
                    {st}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Дата с */}
            <TextField
              size="small"
              type="date"
              label="Дата с"
              value={filters.dateFrom}
              onChange={(e) => onFilterChange({ dateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />

            {/* Дата по */}
            <TextField
              size="small"
              type="date"
              label="Дата по"
              value={filters.dateTo}
              onChange={(e) => onFilterChange({ dateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.dateFrom || undefined }}
              sx={{ width: 150 }}
            />
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};
